import React, {createContext, useContext, useMemo} from 'react'
import {usePageCache} from './with-page-cache'
import ProductRequestMerger from '../utils/product-request-merger'
import {useCommerceAPI} from '../commerce-api/contexts'
import PropTypes from 'prop-types'

const PageProductsCacheContext = createContext(null)

/**
 * Class to handle the cache of products for a page.
 */
class PageProductsCache {
    constructor({getProduct, getProducts, pageCache}) {
        // the api functions to get products
        this._api = {getProduct, getProducts}
        // the page cache to use for caching products
        this._pageCache = pageCache
        // Map of extraParams to queue - use to separate queue per extraParams
        this._queues = new Map()
    }

    /**
     * Get the cache key for the page cache based on the extra params.
     * @param {object} extraParams - the extra params for loading product
     * @return {string} - the cache key
     * @private
     */
    _getPageCacheKey(extraParams) {
        // Sort extraParams to make sure that the same extraParams will always be represented by the same key
        const key = JSON.stringify(
            Object.entries(extraParams).sort(([a], [b]) => a.localeCompare(b))
        )
        return `products(${key})`
    }

    /**
     * Get a single product by id from the cache or the api.
     * @param {string} key - the cache key
     * @param {string} id - the product id
     * @param {object} extraParams - the extra params for loading product
     * @return {Promise<Object>} - the product data
     * @private
     */
    _getProduct(key, id, extraParams) {
        const cachedProduct = this._pageCache.get(key, id)
        if (cachedProduct) {
            return Promise.resolve(cachedProduct)
        }

        if (!this._queues.has(key)) {
            const queue = new ProductRequestMerger({
                ...this._api,
                cache: true,
                extraParams
            })
            this._queues.set(key, queue)
        }
        const queue = this._queues.get(key)

        return queue.getProduct(id).then((data) => {
            this._pageCache.set(key, id, data)
            return data
        })
    }

    /**
     * Get a product by id.
     * @param {string} id - the product id
     * @param {object} extraParams - the extra params for loading product
     * @return {Promise<Object>}
     */
    getProduct = async ({parameters: {id, ...extraParams}}) => {
        const key = this._getPageCacheKey(extraParams)
        return this._getProduct(key, id, extraParams)
    }

    /**
     * Get multiple products by id.
     * @param {string} ids - comma separated list of product ids
     * @param {object} extraParams - the extra params for loading product
     * @return {Promise<{data: Object[]}>}
     */
    getProducts = async ({parameters: {ids, ...extraParams}}) => {
        const key = this._getPageCacheKey(extraParams)
        const data = await Promise.all(
            ids.split(',').map((id) => this._getProduct(key, id, extraParams))
        )
        return {data}
    }
}

const PageProductsCacheProvider = ({children}) => {
    const {
        shopperProducts: {getProduct, getProducts}
    } = useCommerceAPI()
    const pageCache = usePageCache()
    const ctx = useMemo(
        () => new PageProductsCache({getProduct, getProducts, pageCache}),
        [getProduct, getProducts, pageCache]
    )
    return (
        <PageProductsCacheContext.Provider value={ctx}>
            {children}
        </PageProductsCacheContext.Provider>
    )
}

PageProductsCacheProvider.propTypes = {
    children: PropTypes.node.isRequired
}

/**
 * Hook to get the PageProductsCache instance for the current page.
 *
 * @return {PageProductsCache|null}
 */
export function usePageProductsCache() {
    const ctx = useContext(PageProductsCacheContext)
    if (!ctx && process.env.NODE_ENV !== 'production' && process.env.NODE_ENV !== 'test') {
        console.warn(
            'usePageProductsCache should be used within a page wrapped with withPageProductsCache'
        )
    }
    return ctx
}

/**
 * HOC to provide a PageProductsCache instance to a page.
 */
function withPageProductsCache(Page) {
    const WrappedComponent = (props) => (
        <PageProductsCacheProvider>
            <Page {...props} />
        </PageProductsCacheProvider>
    )

    if (process.env.NODE_ENV !== 'production') {
        WrappedComponent.displayName = `withPageProductsCache(${Page.displayName || Page.name})`
    }

    if (Page.getProps) {
        if (Page.shouldGetProps) {
            WrappedComponent.shouldGetProps = Page.shouldGetProps
        }

        WrappedComponent.getProps = (context) => {
            const {
                api: {
                    shopperProducts: {getProduct, getProducts}
                },
                cache: pageCache
            } = context
            const products = new PageProductsCache({getProduct, getProducts, pageCache})
            return Page.getProps({...context, products})
        }
    }

    WrappedComponent.getTemplateName = Page.getTemplateName

    return WrappedComponent
}

export default withPageProductsCache
